import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "./queryKeys";
import {
  listObjectTypes,
  createObjectType,
  updateObjectType,
  deleteObjectType,
} from "@/api/objectTypes";
import { toast } from "sonner";
import { toastFromError } from "@/lib/errors";

export function useObjectTypes(caseId?: string | null) {
  return useQuery({
    queryKey: [...qk.objectTypes(), caseId ?? "all"],
    queryFn: () => listObjectTypes(caseId ?? undefined),
    staleTime: 60_000,
  });
}

export function useCreateObjectType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof createObjectType>[0]) => createObjectType(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.objectTypes() });
      toast.success("Object type created");
    },
    onError: (err) => toastFromError(err),
  });
}

export function useUpdateObjectType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: Parameters<typeof updateObjectType> extends [infer A, infer B] ? { id: A; body: B } : never) =>
      updateObjectType(id, body),
    onSuccess: (data) => {
      qc.setQueryData(qk.objectType(data.id), data);
      qc.invalidateQueries({ queryKey: qk.objectTypes() });
    },
    onError: (err) => toastFromError(err),
  });
}

export function useDeleteObjectType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteObjectType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.objectTypes() });
      toast.success("Object type deleted");
    },
    onError: (err) => toastFromError(err),
  });
}
